import React from 'react';
import Editor from '@monaco-editor/react';
import { Plus, X } from 'lucide-react';
import TriggerMetricFilter, { normalizeInlineFilter } from 'features/agents/WorkflowEditor/TriggerMetricFilter';
import ScheduleEditor from 'features/agents/WorkflowEditor/ScheduleEditor';
import AgentModelField from 'features/agents/WorkflowEditor/AgentModelField';
import AgentToolsField from 'features/agents/WorkflowEditor/AgentToolsField';
import SlackChannelPicker from 'features/agents/WorkflowEditor/SlackChannelPicker';
import SlackResourcePicker from 'features/agents/WorkflowEditor/SlackResourcePicker';
import SlackWorkspaceStatus from 'features/agents/WorkflowEditor/SlackWorkspaceStatus';
import {
  LinearAssigneePicker, LinearPriorityPicker, LinearStatePicker, LinearTeamPicker, LinearWorkspaceStatus,
} from 'features/agents/WorkflowEditor/fields/linear';
import { FIELD_TYPES } from 'features/agents/WorkflowEditor/fields/fieldTypes';
import { McpCredentialPicker, McpToolMultiPicker } from 'features/agents/WorkflowEditor/fields/mcp';
import { AlertMultiPicker, CredentialPicker } from 'features/agents/WorkflowEditor/fields/lookups';
import DatasetPicker from 'features/agents/WorkflowEditor/fields/DatasetPicker';
import {
  DecisionModelField, DecideStateField, DecideQuestionsField, DecideAbstainField, DecideRoutesField,
} from 'features/agents/WorkflowEditor/fields/decide';
import {
  ExpressionField, Field, FieldError, KeyValueList, NumberField, PairList, SecretInput, SelectField, TextArea, TextInput, ToggleField,
  cx, exprDropHandlers, ghostBtnCls, invalidCls, removeBtnCls, useIssue,
} from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * One schema field, by its `type`. The single switch the whole settings form
 * goes through, so a new field type is added in one place.
 */

const MONACO_LANG = { python: 'python', sql: 'sql', json: 'json', markdown: 'markdown' };

function CodeField({ field, value, onChange, theme }) {
  const issue = useIssue(field.key);
  const lang = field.type === 'sql' ? 'sql' : (MONACO_LANG[field.language] || 'plaintext');
  const tall = field.language === 'python' || field.type === 'sql';
  return (
    <Field label={field.label} fieldKey={field.key} className="w-full min-w-0">
      <div
        className={cx(
          'relative w-full min-w-0 max-w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] focus-within:border-[color:var(--border-strong)]',
          issue && invalidCls,
        )}
        {...exprDropHandlers(value, onChange)}
      >
        <Editor
          // Short enough that two code fields still leave the rest of the form
          // reachable in a side panel. Monaco scrolls internally.
          width="100%"
          height={tall ? '220px' : '150px'}
          language={lang}
          // A JSON field may hold an object (a template or the Datasets page wrote it);
          // show it as text. The node reads either shape back.
          value={value != null && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value ?? '')}
          onChange={(v) => onChange(v ?? '')}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          options={{
            fontSize: 12,
            fontFamily: 'JetBrains Mono, Menlo, Consolas, monospace',
            lineNumbers: 'on',
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: lang === 'python' ? 4 : 2,
            insertSpaces: true,
            wordWrap: 'on',
            renderLineHighlight: 'line',
            bracketPairColorization: { enabled: true },
            folding: true,
            padding: { top: 8, bottom: 8 },
            suggestOnTriggerCharacters: true,
            quickSuggestions: { other: true, comments: false, strings: false },
          }}
        />
      </div>
    </Field>
  );
}

function AssignmentsEditor({ field, value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  return (
    <Field label={field.label} fieldKey={field.key}>
      <PairList
        pairs={list.map((a) => ({ key: a.key || '', expression: a.expression || '' }))}
        aKey="key"
        bKey="expression"
        aPlaceholder="field"
        bPlaceholder="{{ $json.value }}"
        bMono
        addLabel="Add field"
        onChange={onChange}
      />
    </Field>
  );
}

const RELATIVE_WINDOWS = [
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

function TimeWindowEditor({ field, value, onChange }) {
  const v = value || { kind: 'relative', value: '7d' };
  return (
    <Field label={field.label} fieldKey={field.key}>
      <div className="flex gap-1.5">
        <div className="w-[104px] shrink-0">
          <SelectField value={v.kind} onChange={(kind) => onChange({ ...v, kind })} options={[{ value: 'relative', label: 'Last' }, { value: 'absolute', label: 'Between' }]} ariaLabel="Window type" />
        </div>
        {v.kind === 'relative' ? (
          <div className="min-w-0 flex-1">
            <SelectField value={v.value || '7d'} onChange={(val) => onChange({ ...v, value: val })} options={RELATIVE_WINDOWS} ariaLabel="Window length" />
          </div>
        ) : (
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-1.5">
            <TextInput type="datetime-local" aria-label="From" value={v.start || ''} onChange={(start) => onChange({ ...v, start })} drop={false} />
            <TextInput type="datetime-local" aria-label="To" value={v.end || ''} onChange={(end) => onChange({ ...v, end })} drop={false} />
          </div>
        )}
      </div>
    </Field>
  );
}

/* ── optional (opt-in) fields ───────────────────────────────────────────── */

const EMPTY_SUB_ISSUE = { issue_id: '', title: '', description: '', state_id: '', priority: null, assignee_id: null, extra_fields: [] };
const EMPTY_SUB_ISSUE_UPDATE = { issue_id: '', title: '', description: '', state_id: '', priority: null, assignee_id: null, update_fields: [] };

function SectionTitle({ children }) {
  return <div className="border-b border-[color:var(--border-default)] pb-1.5 text-[12px] font-semibold text-[color:var(--text-primary)]">{children}</div>;
}

function RemovableRow({ onRemove, title, children }) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="min-w-0 flex-1">{children}</div>
      <button type="button" onClick={onRemove} title={title} aria-label={title} className={cx(removeBtnCls, 'mt-[22px]')}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AddFieldMenu({ options, onAdd, placeholder = 'Add field' }) {
  if (!options.length) return null;
  return <SelectField value="" onChange={onAdd} options={options} placeholder={placeholder} />;
}

/**
 * Fields flagged `optional: true` (a model's max tokens, temperature...). None
 * show by default; "Add parameter" reveals one and the remove button clears it.
 * An unset parameter is omitted from the request so the provider defaults it.
 * Enabled keys live in `cfg._params`; nodes saved before that existed infer
 * the set from which optional fields already hold a value.
 */
function OptionalParamsSection({ fields, cfg, updateField, updateFields, theme, nodeKind }) {
  if (!fields || !fields.length) return null;
  const hasValue = (key) => cfg[key] != null && cfg[key] !== '';
  const enabled = Array.isArray(cfg._params)
    ? cfg._params.filter((k) => fields.some((f) => f.key === k))
    : fields.filter((f) => hasValue(f.key)).map((f) => f.key);
  const available = fields.filter((f) => !enabled.includes(f.key)).map((f) => ({ value: f.key, label: f.label }));
  const add = (key) => {
    const f = fields.find((x) => x.key === key);
    if (!f || enabled.includes(key)) return;
    updateFields({ _params: [...enabled, key], [key]: f.optionalDefault ?? '' });
  };
  const remove = (key) => updateFields({ _params: enabled.filter((k) => k !== key), [key]: '' });

  return (
    <div className="space-y-3 pt-1">
      <SectionTitle>Parameters</SectionTitle>
      {enabled.length === 0 ? <p className="text-[11px] text-[color:var(--text-muted)]">Using the defaults. Add a parameter to override one.</p> : null}
      <div className="space-y-4">
        {enabled.map((key) => {
          const def = fields.find((f) => f.key === key);
          if (!def) return null;
          return (
            <RemovableRow key={key} onRemove={() => remove(key)} title="Remove parameter">
              <FieldRow field={def} cfg={cfg} value={cfg[key]} onChange={(v) => updateField(key, v)} onSiblingChange={(k, v) => updateField(k, v)} onBatch={updateFields} theme={theme} nodeKind={nodeKind} />
            </RemovableRow>
          );
        })}
      </div>
      <AddFieldMenu options={available} onAdd={add} placeholder="Add parameter" />
    </div>
  );
}

function OptionalFieldsBlock({ label = 'Additional fields', optionalFields, values, storageKey, rowCfg, teamField, theme, onFieldChange, onStorageChange, nested = false }) {
  const stored = Array.isArray(values[storageKey]) ? values[storageKey] : [];
  const hasValue = (key) => values[key] != null && values[key] !== '';
  const enabled = stored.length > 0 ? stored : optionalFields.filter((f) => hasValue(f.key)).map((f) => f.key);
  const available = optionalFields.filter((f) => !enabled.includes(f.key)).map((f) => ({ value: f.key, label: f.label }));
  const add = (key) => { if (key && !enabled.includes(key)) onStorageChange([...enabled, key]); };
  const remove = (key) => {
    const def = optionalFields.find((f) => f.key === key);
    const patch = { [storageKey]: enabled.filter((k) => k !== key) };
    patch[key] = def?.type === 'linear_priority' || def?.type === 'linear_assignee' ? null : '';
    onFieldChange(patch);
  };
  return (
    <div className={cx('space-y-3', !nested && 'pt-1')}>
      {nested ? <div className="text-[12px] text-[color:var(--text-muted)]">{label}</div> : <SectionTitle>{label}</SectionTitle>}
      <div className="space-y-4">
        {enabled.map((key) => {
          const def = optionalFields.find((f) => f.key === key);
          if (!def) return null;
          return (
            <RemovableRow key={key} onRemove={() => remove(key)} title="Remove field">
              <FieldRow
                field={{ ...def, teamField: def.teamField || teamField }}
                cfg={rowCfg}
                value={values[key]}
                onChange={(v) => onFieldChange({ [key]: v })}
                onSiblingChange={(k, v) => onFieldChange({ [k]: v })}
                onBatch={onFieldChange}
                theme={theme}
              />
            </RemovableRow>
          );
        })}
      </div>
      <AddFieldMenu options={available} onAdd={add} />
    </div>
  );
}

function LinearSubIssuesEditor({ field, cfg, value, onChange, theme }) {
  const list = Array.isArray(value) ? value : [];
  const update = field.mode === 'update';
  const optionalFields = field.optionalFields || [];
  const storageKey = update ? 'update_fields' : 'extra_fields';
  const empty = update ? EMPTY_SUB_ISSUE_UPDATE : EMPTY_SUB_ISSUE;
  const setAt = (idx, patch) => onChange(list.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-3 pt-1">
      <SectionTitle>{field.label}</SectionTitle>
      <div className="space-y-4">
        {list.map((sub, idx) => (
          <div key={idx} className="space-y-3">
            <RemovableRow onRemove={() => onChange(list.filter((_, i) => i !== idx))} title="Remove sub-issue">
              <Field label={update ? 'Issue ID' : 'Title'}>
                <TextInput
                  value={(update ? sub.issue_id : sub.title) || ''}
                  onChange={(v) => setAt(idx, update ? { issue_id: v } : { title: v })}
                  placeholder={update ? 'TRO-187 or a UUID' : 'Sub-issue title'}
                  spellCheck={false}
                />
              </Field>
            </RemovableRow>
            {optionalFields.length > 0 ? (
              <div className={update ? undefined : 'pl-5'}>
                <OptionalFieldsBlock
                  label={update ? 'Update fields' : 'Additional fields'}
                  optionalFields={optionalFields}
                  values={sub}
                  storageKey={storageKey}
                  rowCfg={{ ...cfg, ...sub }}
                  teamField={field.teamField || 'team_id'}
                  theme={theme}
                  nested={!update}
                  onFieldChange={(patch) => setAt(idx, patch)}
                  onStorageChange={(keys) => setAt(idx, { [storageKey]: keys })}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...list, { ...empty }])} className={ghostBtnCls}>
        <Plus className="h-3 w-3" aria-hidden /> Add sub-issue
      </button>
    </div>
  );
}

function AdditionalFieldsPicker({ field, cfg, onBatch, onSiblingChange, theme }) {
  const storageKey = field.storageKey || 'extra_fields';
  return (
    <OptionalFieldsBlock
      label={field.label}
      optionalFields={field.fields || []}
      values={cfg}
      storageKey={storageKey}
      rowCfg={cfg}
      teamField="team_id"
      theme={theme}
      onFieldChange={(patch) => {
        const keys = Object.keys(patch);
        if (keys.length === 1 && keys[0] !== storageKey) onSiblingChange(keys[0], patch[keys[0]]);
        else onBatch(patch);
      }}
      onStorageChange={(keys) => onBatch({ [storageKey]: keys })}
    />
  );
}

/* ── the switch ─────────────────────────────────────────────────────────── */

export { OptionalParamsSection };

export { FIELD_TYPES };

export function FieldRow({ field, cfg, value, onChange, onSiblingChange, onBatch, theme, nodeKind }) {
  const issue = useIssue(field.key);
  switch (field.type) {
    case 'text':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <TextInput value={value} onChange={onChange} placeholder={field.placeholder} invalid={!!issue} />
        </Field>
      );
    case 'password':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <SecretInput value={value} onChange={onChange} placeholder={field.placeholder} invalid={!!issue} />
        </Field>
      );
    case 'number':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <NumberField value={value} onChange={onChange} placeholder={field.placeholder} min={field.min} max={field.max} step={field.step ?? 1} invalid={!!issue} />
        </Field>
      );
    case 'checkbox':
      return <ToggleField label={field.label} value={value} onChange={onChange} help={field.help} />;
    case 'schedule':
      return <ScheduleEditor cron={cfg.cron ?? value} timezone={cfg.timezone} scheduleMode={cfg.schedule_mode} onChange={onChange} onSiblingChange={onSiblingChange} onBatch={onBatch} />;
    case 'select':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <SelectField value={value} onChange={onChange} options={field.options || []} invalid={!!issue} />
        </Field>
      );
    case 'multiline':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <TextArea grow value={value} onChange={onChange} placeholder={field.placeholder} invalid={!!issue} spellCheck />
        </Field>
      );
    case 'expression':
      return (
        <ExpressionField
          label={field.label}
          fieldKey={field.key}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || '{{ $json.field }}'}
          help={field.help}
        />
      );
    case 'code':
    case 'sql':
      return <CodeField field={field} value={value} onChange={onChange} theme={theme} />;
    case 'lines':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <TextArea
            mono
            rows={3}
            value={Array.isArray(value) ? value.join('\n') : (value || '')}
            onChange={(v) => onChange(v.split('\n').map((s) => s.trim()).filter(Boolean))}
            spellCheck={false}
            drop={false}
            placeholder="One per line"
          />
        </Field>
      );
    case 'kv':
      return <Field label={field.label} fieldKey={field.key}><KeyValueList value={value || {}} onChange={onChange} /></Field>;
    case 'assignments':
      return <AssignmentsEditor field={field} value={value} onChange={onChange} />;
    case 'time_window':
      return <TimeWindowEditor field={field} value={value} onChange={onChange} />;
    case 'credential':
      return <CredentialPicker field={field} value={value} onChange={onChange} />;
    case 'slack_channel':
      return <SlackChannelPicker field={field} value={value} onChange={onChange} nodeKind={nodeKind} />;
    case 'slack_user':
    case 'slack_users':
    case 'slack_message':
    case 'slack_file':
    case 'slack_usergroup':
    case 'slack_emoji':
      return <SlackResourcePicker field={field} value={value} onChange={onChange} cfg={cfg} />;
    case 'slack_workspace_status':
      return <SlackWorkspaceStatus />;
    case 'linear_workspace_status':
      return <LinearWorkspaceStatus />;
    case 'linear_team':
      return <LinearTeamPicker field={field} value={value} onChange={onChange} />;
    case 'linear_state':
      return <LinearStatePicker field={field} value={value} onChange={onChange} linearTeamId={cfg[field.teamField || 'team_id']} />;
    case 'linear_assignee':
      return <LinearAssigneePicker field={field} value={value} onChange={onChange} linearTeamId={cfg[field.teamField || 'team_id']} />;
    case 'linear_priority':
      return <LinearPriorityPicker field={field} value={value} onChange={onChange} />;
    case 'linear_sub_issues':
      return <LinearSubIssuesEditor field={field} cfg={cfg} value={value} onChange={onChange} theme={theme} />;
    case 'additional_fields':
      return <AdditionalFieldsPicker field={field} cfg={cfg} onBatch={onBatch} onSiblingChange={onSiblingChange} theme={theme} />;
    case 'mcp_credential':
      return <McpCredentialPicker field={field} value={value} onChange={onChange} />;
    case 'mcp_tools':
      return <McpToolMultiPicker field={field} value={value} onChange={onChange} credentialId={cfg.credential_id} />;
    case 'agent_model':
      return (
        <div>
          <AgentModelField value={value} onChange={onChange} />
          <FieldError>{issue}</FieldError>
        </div>
      );
    case 'agent_tools':
      return <AgentToolsField value={value || []} onChange={onChange} theme={theme} />;
    case 'dataset_picker':
      return (
        <Field label={field.label} fieldKey={field.key} help={field.help}>
          <DatasetPicker value={value} onChange={onChange} invalid={!!issue} allowNew={field.allowNew !== false} />
        </Field>
      );
    case 'alert_multi_picker': {
      const ids = Array.isArray(cfg.alert_ids) && cfg.alert_ids.length ? cfg.alert_ids : (cfg.alert_id ? [cfg.alert_id] : []);
      // Writing the list retires the single id, so the two never disagree.
      const set = (next) => (onBatch ? onBatch({ alert_ids: next, alert_id: null }) : onChange(next));
      return <AlertMultiPicker field={field} value={ids} onChange={set} />;
    }
    case 'entity_filters': {
      // Metric picker (event / run / span) plus the cross-entity global filter,
      // the same components Insights and Activity use.
      const entityKey = field.entityField || 'entity';
      const eventKey = field.eventNameField || 'event_name';
      const initial = {
        entity: cfg[entityKey] || 'events',
        event_name: cfg[eventKey] || '',
        filters: normalizeInlineFilter(value),
        global_filters: cfg.global_filters || value?.global_filters || [],
      };
      const handleChange = (patch) => {
        // One config write for entity + event_name + filters: back-to-back
        // writes would each read the original cfg and overwrite each other.
        const next = { ...initial, ...patch };
        const normalized = normalizeInlineFilter(next.filters);
        onBatch({
          [entityKey]: next.entity,
          [eventKey]: next.event_name,
          [field.key]: { match: normalized.match, clauses: normalized.clauses },
          // Hoisted so publish and the dispatcher see globals at top level.
          global_filters: next.global_filters || [],
        });
      };
      return <TriggerMetricFilter value={initial} onChange={handleChange} />;
    }
    case 'decision_model':
      return <DecisionModelField value={value} onChange={onChange} />;
    case 'decision_state':
      return <DecideStateField value={value} onChange={onChange} />;
    case 'decision_questions':
      return <DecideQuestionsField value={value} cfg={cfg} onBatch={onBatch} />;
    case 'decision_abstain':
      return <DecideAbstainField cfg={cfg} onBatch={onBatch} />;
    case 'decision_routes':
      return <DecideRoutesField value={value} cfg={cfg} onChange={onChange} />;
    default:
      return (
        <Field label={field.label}>
          <p className="text-[12px] text-[color:var(--text-muted)]">Unsupported field type: {field.type}</p>
        </Field>
      );
  }
}
